import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer, fromEvent, merge, of } from 'rxjs';
import { map, debounceTime, distinctUntilChanged, tap, switchMap, retry, timeout, catchError } from 'rxjs/operators';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { Network } from '@capacitor/network';
import { ConnectionResult, ConnectionState, QueuedRegistration } from '@interfaces/ConnectionManagerInterface/connectionManager';
@Injectable({
  providedIn: 'root'
})
export class ConnectionManagerService {
  private readonly CONNECTION_CHECK_INTERVAL = 30000; // 30 seconds
  private readonly FIRESTORE_TIMEOUT = 3000; // 3 seconds
  private readonly MAX_RETRY_ATTEMPTS = 5;
  private readonly RETRY_DELAY_BASE = 1000; // 1 second
  private connectionState$ = new BehaviorSubject<ConnectionState>({
    isOnline: true,
    isFirestoreConnected: false,
    connectionQuality: 'unknown',
    lastChecked: new Date(),
    consecutiveFailures: 0
  });
  private registrationQueue$ = new BehaviorSubject<QueuedRegistration[]>([]);
  private isProcessingQueue = false;
  public connectionStateChange$ = this.connectionState$.asObservable();
  public queuedRegistrations$ = this.registrationQueue$.asObservable();
  constructor(private firestore: Firestore) {

    this.initializeConnectionMonitoring();
    this.loadQueueFromStorage();
  }
  /**
    * Initialize connection monitoring
    */
  private initializeConnectionMonitoring(): void {
    // Monitor browser online/offline events
    const onlineEvent$ = fromEvent(window, 'online').pipe(map(() => true));
    const offlineEvent$ = fromEvent(window, 'offline').pipe(map(() => false));

    // Combine browser events
    merge(onlineEvent$, offlineEvent$)
      .pipe(debounceTime(500))
      .subscribe(isOnline => {
        this.updateConnectionState({ isOnline });
        if (isOnline) {
          this.checkFirestoreConnection();
          this.processQueue();
        }
      });

    // Monitor Capacitor Network plugin
    Network.addListener('networkStatusChange', (status) => {
      this.updateConnectionState({
        isOnline: status.connected,
        connectionQuality: this.mapConnectionType(status.connectionType)
      });

      if (status.connected) {
        this.checkFirestoreConnection();
        this.processQueue();
      }
    });

    // Periodic connection quality check
    timer(0, this.CONNECTION_CHECK_INTERVAL).subscribe(() => {
      this.performConnectionCheck();
    });
  }
  /**
  * Check connection with fallback data
  */
  async checkConnectionWithFallback(): Promise<ConnectionResult> {
    const currentState = this.connectionState$.value;

    // Step 1: Basic online check
    if (!navigator.onLine) {
      return {
        canProceed: true,
        isFullyOnline: false,
        fallbackMode: true,
        essentialData: this.getCachedEssentials(),
        connectionQuality: 'offline'
      };
    }

    // Step 2: Quick Firestore check
    const firestoreConnected = await this.checkFirestoreConnection();

    if (firestoreConnected) {
      return {
        canProceed: true,
        isFullyOnline: true,
        fallbackMode: false,
        essentialData: null,
        connectionQuality: currentState.connectionQuality
      };
    }

    // Step 3: Fallback mode
    return {
      canProceed: true,
      isFullyOnline: false,
      fallbackMode: true,
      essentialData: this.getCachedEssentials(),
      connectionQuality: 'poor'
    };
  }

  /**
   * Check Firestore connectivity
   */
  private async checkFirestoreConnection(): Promise<boolean> {
    try {
      // Use a lightweight system document
      const testDoc = doc(this.firestore, 'system/health');

      const docSnap = await Promise.race([
        getDoc(testDoc),
        new Promise<null>((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), this.FIRESTORE_TIMEOUT)
        )
      ]);

      const isConnected = docSnap !== null;

      this.updateConnectionState({
        isFirestoreConnected: isConnected,
        consecutiveFailures: isConnected ? 0 : this.connectionState$.value.consecutiveFailures + 1
      });

      return isConnected;
    } catch (error) {
      this.updateConnectionState({
        isFirestoreConnected: false,
        consecutiveFailures: this.connectionState$.value.consecutiveFailures + 1
      });
      return false;
    }
  }

  /**
   * Perform comprehensive connection check
   */
  private async performConnectionCheck(): Promise<void> {
    const start = Date.now();

    try {
      // Test Google connectivity
      const googleResponse = await this.testEndpoint('https://www.google.com/favicon.ico');

      // Test Firestore
      const firestoreConnected = await this.checkFirestoreConnection();

      const latency = Date.now() - start;
      const quality = this.calculateConnectionQuality(latency, googleResponse, firestoreConnected);

      // Log the connection quality
      console.log('Connection Quality:', quality);

      this.updateConnectionState({
        connectionQuality: quality,
        lastChecked: new Date()
      });

    } catch (error) {
      this.updateConnectionState({
        connectionQuality: 'poor',
        consecutiveFailures: this.connectionState$.value.consecutiveFailures + 1
      });
    }
  }


  /**
   * Test endpoint connectivity
   */
  private async testEndpoint(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache'
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Calculate connection quality based on metrics
   */
  private calculateConnectionQuality(
    latency: number,
    googleReachable: boolean,
    firestoreConnected: boolean
  ): 'good' | 'poor' | 'offline' {
    if (!googleReachable) return 'offline';
    if (!firestoreConnected) return 'poor';
    if (latency < 1000) return 'good';
    if (latency < 3000) return 'poor';
    return 'poor';
  }

  /**
   * Queue registration for offline processing
   */
  async queueRegistration(formData: any): Promise<string> {
    const queuedData = JSON.parse(localStorage.getItem('offline_registrations') || '[]');
    const entry = {
      id: Date.now().toString(),
      formData,
      timestamp: new Date().toISOString()
    };
    queuedData.push(entry);
    localStorage.setItem('offline_registrations', JSON.stringify(queuedData));
    return entry.id;
  }


  /**
   * Process queued registrations
   */
  async processQueue(): Promise<void> {
    if (this.isProcessingQueue || !this.connectionState$.value.isOnline) {
      return;
    }

    this.isProcessingQueue = true;
    const queue = [...this.registrationQueue$.value];

    for (const item of queue) {
      if (item.attempts >= this.MAX_RETRY_ATTEMPTS) {
        continue; // Skip items that have exceeded retry limit
      }

      try {
        // Update attempt info
        item.attempts++;
        item.lastAttempt = Date.now();

        // Process with exponential backoff
        const delay = this.calculateBackoffDelay(item.attempts);
        await this.delay(delay);

        // Attempt to process registration
        const success = await this.processRegistration(item.data);

        if (success) {
          // Remove from queue
          this.removeFromQueue(item.id);
        } else {
          // Update error info
          item.error = 'Processing failed';
          this.updateQueueItem(item);
        }

      } catch (error) {
        item.error = error.message;
        this.updateQueueItem(item);
      }
    }

    this.isProcessingQueue = false;
  }

  /**
   * Process individual registration (implement based on your registration logic)
   */
  private async processRegistration(data: any): Promise<boolean> {
    // This should call your actual registration service
    // For now, returning a placeholder
    console.log('Processing registration:', data);
    return true;
  }

  /**
   * Calculate exponential backoff delay
   */
  private calculateBackoffDelay(attemptNumber: number): number {
    return Math.min(
      this.RETRY_DELAY_BASE * Math.pow(2, attemptNumber - 1),
      30000 // Max 30 seconds
    );
  }

  /**
   * Get cached essential data
   */
  private getCachedEssentials(): any {
    try {
      const cached = localStorage.getItem('lotto_essential_data');
      if (cached) {
        const data = JSON.parse(cached);
        // Use if less than 7 days old
        if (Date.now() - data.timestamp < 604800000) {
          return data;
        }
      }
    } catch (error) {
      return error;
    }

    // Return defaults
    return {
      languages: [{
        language_code: "en",
        language_name: "English",
        language_status: true,
        selected: true,
        country_code: "44"
      }],
      currencies: [{
        currency_code: "GBP",
        currency_name: "British Pound",
        status: true,
        selected: true,
        currency_symbol: "£"
      }]
    };
  }

  /**
   * Cache essential data
   */
  cacheEssentialData(data: any): void {
    try {
      const cacheData = {
        ...data,
        timestamp: Date.now()
      };
      localStorage.setItem('lotto_essential_data', JSON.stringify(cacheData));
      console.log("the data set in localStorage ");
    } catch (error) {
      return error;
    }
  }

  /**
   * Utility methods
   */
  private updateConnectionState(partial: Partial<ConnectionState>): void {
    this.connectionState$.next({
      ...this.connectionState$.value,
      ...partial
    });
  }

  private mapConnectionType(type: string): 'good' | 'poor' | 'offline' {
    switch (type) {
      case 'wifi':
      case '4g':
        return 'good';
      case '3g':
      case '2g':
        return 'poor';
      case 'none':
        return 'offline';
      default:
        return 'unknown' as any;
    }
  }

  private generateQueueId(): string {
    return `reg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Queue persistence methods
   */
  private async loadQueueFromStorage(): Promise<void> {
    try {
      const stored = localStorage.getItem('lotto_registration_queue');
      if (stored) {
        const queue = JSON.parse(stored);
        this.registrationQueue$.next(queue);
      }
    } catch (error) {
      return error;
    }
  }

  private async saveQueueToStorage(queue: QueuedRegistration[]): Promise<void> {
    try {
      localStorage.setItem('lotto_registration_queue', JSON.stringify(queue));
    } catch (error) {
      return error;
    }
  }

  private removeFromQueue(id: string): void {
    const updated = this.registrationQueue$.value.filter(item => item.id !== id);
    this.registrationQueue$.next(updated);
    this.saveQueueToStorage(updated);
  }

  private updateQueueItem(item: QueuedRegistration): void {
    const queue = this.registrationQueue$.value;
    const index = queue.findIndex(q => q.id === item.id);

    if (index !== -1) {
      queue[index] = item;
      this.registrationQueue$.next([...queue]);
      this.saveQueueToStorage(queue);
    }
  }

  /**
   * Public API methods
   */
  getConnectionState(): ConnectionState {
    return this.connectionState$.value;
  }

  getQueuedCount(): number {
    return this.registrationQueue$.value.length;
  }

  clearQueue(): void {
    this.registrationQueue$.next([]);
    localStorage.removeItem('lotto_registration_queue');
  }

  retryFailedItems(): void {
    const queue = this.registrationQueue$.value;
    queue.forEach(item => {
      if (item.attempts >= this.MAX_RETRY_ATTEMPTS) {
        item.attempts = this.MAX_RETRY_ATTEMPTS - 1; // Allow one more retry
      }
    });
    this.registrationQueue$.next([...queue]);
    this.processQueue();
  }




}


